import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Language {
  code: string;
  name: string;
  nativeName: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'crk', name: 'Cree', nativeName: 'Nehiyawewin' },
  { code: 'oj', name: 'Ojibwe', nativeName: 'Anishinaabemowin' },
  { code: 'iu', name: 'Inuktitut', nativeName: 'Inuktitut' },
  { code: 'nv', name: 'Navajo', nativeName: 'Dine Bizaad' },
  { code: 'chr', name: 'Cherokee', nativeName: 'Tsalagi' },
];

const LanguageSelector: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Normalize language code (e.g., 'en-US' -> 'en')
  const normalizeLanguage = (lang: string): string => {
    return lang.split('-')[0].toLowerCase();
  };

  // Update current language when i18n language changes
  useEffect(() => {
    const updateLanguage = () => {
      const normalized = normalizeLanguage(i18n.language);
      setCurrentLang(normalized);
    };
    
    updateLanguage();
    i18n.on('languageChanged', updateLanguage);
    
    return () => {
      i18n.off('languageChanged', updateLanguage);
    };
  }, [i18n]);

  const currentLanguage = languages.find(lang => lang.code === normalizeLanguage(currentLang)) || languages[0];

  const changeLanguage = async (languageCode: string) => {
    try {
      await i18n.changeLanguage(languageCode);
      localStorage.setItem('i18nextLng', languageCode);
      setCurrentLang(languageCode);
      setIsOpen(false);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 p-2 text-text-muted hover:text-text-primary transition-colors"
        title={t('language.selectLanguage')}
        aria-label={t('language.selectLanguage')}
      >
        <Globe className="w-5 h-5" />
        <span className="hidden md:inline text-sm">{currentLanguage.code.toUpperCase()}</span>
        <ChevronDown className="w-4 h-4 hidden md:inline" />
      </button>

      {isOpen && (
        <div className="absolute top-12 right-0 w-48 md:w-56 bg-dark-bg border border-dark-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="py-1">
            <div className="px-4 py-2 text-xs font-semibold text-text-muted uppercase tracking-wide border-b border-dark-border">
              {t('language.selectLanguage')}
            </div>
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => changeLanguage(language.code)}
                className={`w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-dark-card transition-colors ${
                  normalizeLanguage(currentLang) === language.code
                    ? 'bg-dark-card text-accent-blue'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <div className="flex flex-col items-start">
                  <span className="font-medium">{language.nativeName}</span>
                  <span className="text-xs text-text-muted">{language.name}</span>
                </div>
                {normalizeLanguage(currentLang) === language.code && (
                  <div className="w-2 h-2 bg-accent-blue rounded-full"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;

